// Music and ambience (issue 1161). The pure half: what the table is playing, the DM's events that change it, and the
// one question the server asks before it hands a player a track's bytes — is it in play at their table now?
//
// The sound is TABLE-WIDE, held on the table's own log (like the handouts and the rulings), so it carries on across a
// scene change. Two channels hold a playlist each — `music` and `ambience` — and a third, `effect`, holds the one-off
// sounds lately fired. Everyone at the table hears the same thing; each person's volume is their own browser's (never
// sent). Nothing here is secret from the table: the state names only what is playing or queued, so a track the DM
// uploaded but never played is in no state, no frame and no player's fetch.
//
// A playlist moves on when its track ends: the client of whoever holds the power (the DM, or a co-DM with `notes`)
// sends `audio.next { from }`, and the server takes it only while the playlist still stands at `from`, so several
// screens saying it at once move it once. `startAt` is the server's clock when a track began; each screen plays from
// `now - startAt`, close enough to a few seconds.
export const AUDIO_CHANNELS = ['music', 'ambience', 'effect'];
export const PLAYLIST_CHANNELS = ['music', 'ambience'];
/**
 * The bounds: a playlist's length, the effects remembered, a track's name, a file's size, the account's total, and how
 * long after it was fired an effect may still be fetched.
 */
export const AUDIO_LIMITS = {
    tracks: 50,
    effects: 8,
    name: 120,
    bytes: 25 * 1024 * 1024,
    account: 100 * 1024 * 1024,
    effectWindowMs: 10 * 60 * 1000,
};
/** The formats the server can tell from a file's bytes (`server/src/sounds.ts`). */
export const AUDIO_FORMATS = ['mp3', 'ogg', 'opus', 'wav', 'm4a', 'aac'];
export const emptyAudio = () => ({ music: null, ambience: null, effects: [] });
export const AUDIO_KINDS = ['audio.play', 'audio.next', 'audio.stop'];
export const isAudioBody = (b) => AUDIO_KINDS.includes(b.kind);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => typeof v === 'string' && UUID_RE.test(v);
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const LINE_BAD = /[\u0000-\u001f\u007f]/;
/** A track's name: one line, 1–120 characters. */
export const isTrackName = (v) => typeof v === 'string' && v.trim().length >= 1 && v.length <= AUDIO_LIMITS.name && !LINE_BAD.test(v);
const isTime = (v) => typeof v === 'string' && v.length <= 40 && !Number.isNaN(Date.parse(v));
const isChannel = (v) => v === 'music' || v === 'ambience' || v === 'effect';
const isPlaylistChannel = (v) => v === 'music' || v === 'ambience';
export function parseAudioBody(b) {
    switch (b.kind) {
        case 'audio.play': {
            if (!isChannel(b.channel) || typeof b.loop !== 'boolean')
                return null;
            const max = b.channel === 'effect' ? 1 : AUDIO_LIMITS.tracks;
            if (!Array.isArray(b.tracks) || b.tracks.length < 1 || b.tracks.length > max || !b.tracks.every(isUuid))
                return null;
            if (b.channel === 'effect' && b.loop)
                return null;
            if (b.names !== undefined && (!Array.isArray(b.names) || b.names.length !== b.tracks.length || !b.names.every(isTrackName)))
                return null;
            if (b.startAt !== undefined && !isTime(b.startAt))
                return null;
            return {
                kind: 'audio.play', channel: b.channel, tracks: [...b.tracks], loop: b.loop,
                ...(b.names !== undefined ? { names: [...b.names] } : {}), ...(b.startAt !== undefined ? { startAt: b.startAt } : {}),
            };
        }
        case 'audio.next': {
            if (!isPlaylistChannel(b.channel) || typeof b.from !== 'number' || !Number.isInteger(b.from) || b.from < 0 || b.from >= AUDIO_LIMITS.tracks)
                return null;
            if (b.startAt !== undefined && !isTime(b.startAt))
                return null;
            if (b.at !== undefined && !isTime(b.at))
                return null;
            return { kind: 'audio.next', channel: b.channel, from: b.from, ...(b.at !== undefined ? { at: b.at } : {}), ...(b.startAt !== undefined ? { startAt: b.startAt } : {}) };
        }
        case 'audio.stop': return isChannel(b.channel) ? { kind: 'audio.stop', channel: b.channel } : null;
        default: return null;
    }
}
/** Why an act on the sound cannot stand, in words, or null. Read against the state before it. */
export function audioRefusal(audio, body) {
    if (body.kind !== 'audio.next')
        return null;
    const on = (audio ?? emptyAudio())[body.channel];
    if (!on || on.index !== body.from || body.at !== on.startAt)
        return 'That track has already moved on.';
    return null;
}
const EPOCH = new Date(0).toISOString();
const copyPlay = (p) => (p ? { ...p, tracks: p.tracks.map((t) => ({ ...t })) } : null);
export function reduceAudio(audio, body) {
    const cur = audio ?? emptyAudio();
    const next = { music: copyPlay(cur.music), ambience: copyPlay(cur.ambience), effects: cur.effects.map((e) => ({ ...e })) };
    switch (body.kind) {
        case 'audio.play': {
            const tracks = body.tracks.map((id, i) => ({ id, name: body.names?.[i] ?? 'A sound' }));
            const startAt = body.startAt ?? EPOCH;
            if (body.channel === 'effect')
                next.effects = [...next.effects, { ...tracks[0], startAt }].slice(-AUDIO_LIMITS.effects);
            else
                next[body.channel] = { tracks, index: 0, loop: body.loop, startAt };
            return next;
        }
        case 'audio.next': {
            const on = next[body.channel];
            if (!on || on.index !== body.from)
                return next;
            const index = on.index + 1;
            if (index < on.tracks.length)
                next[body.channel] = { ...on, index, startAt: body.startAt ?? EPOCH };
            else
                next[body.channel] = on.loop ? { ...on, index: 0, startAt: body.startAt ?? EPOCH } : null;
            return next;
        }
        case 'audio.stop':
            if (body.channel === 'effect')
                next.effects = [];
            else
                next[body.channel] = null;
            return next;
    }
}
/** An older state, a stored one, or a projected one: each part read through, anything unreadable dropped. */
export function normaliseAudio(v) {
    if (!isObj(v))
        return emptyAudio();
    const ref = (t) => (isObj(t) && isUuid(t.id) && isTrackName(t.name) ? { id: t.id, name: t.name } : null);
    const play = (p) => {
        if (!isObj(p) || !Array.isArray(p.tracks) || typeof p.loop !== 'boolean' || !isTime(p.startAt))
            return null;
        const tracks = p.tracks.map(ref).filter((t) => t !== null);
        const index = typeof p.index === 'number' && Number.isInteger(p.index) ? p.index : 0;
        return tracks.length && index >= 0 && index < tracks.length ? { tracks, index, loop: p.loop, startAt: p.startAt } : null;
    };
    const effects = Array.isArray(v.effects)
        ? v.effects.flatMap((e) => { const r = ref(e); return r && isObj(e) && isTime(e.startAt) ? [{ ...r, startAt: e.startAt }] : []; }).slice(-AUDIO_LIMITS.effects)
        : [];
    return { music: play(v.music), ambience: play(v.ambience), effects };
}
/** Is this track playing or queued at the table, `now` (ms, the server's clock)? An effect counts for its window after firing. */
export function audioInPlay(audio, id, now) {
    if (!audio)
        return false;
    for (const ch of PLAYLIST_CHANNELS)
        if (audio[ch]?.tracks.some((t) => t.id === id))
            return true;
    return audio.effects.some((e) => e.id === id && now - Date.parse(e.startAt) < AUDIO_LIMITS.effectWindowMs);
}
/** "3.4 MB": megabytes (of 1024 × 1024 bytes) to one decimal; the room left is rounded DOWN so it is never overstated. */
export const megabytes = (bytes, round = 'near') => {
    const mb = bytes / (1024 * 1024);
    return `${(round === 'down' ? Math.floor(mb * 10) / 10 : mb).toFixed(1)} MB`;
};
/** The refusal of an upload that would take the account over its total: how much room is left, and how big the file is. */
export const audioRoomRefusal = (used, size) => `You have ${megabytes(Math.max(0, AUDIO_LIMITS.account - used), 'down')} of audio room left; this file is ${megabytes(size)}.`;
//# sourceMappingURL=audio.js.map